import {
  forwardRef,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  useEffect,
  useId,
  useState,
} from "react";
import { cn } from "../../utils/cn";

type DsTextAreaProps = {
  errorMessage?: string;
} & Omit<
  ComponentPropsWithoutRef<"textarea">,
  "children" | "className"
> & {
    className?: string;
    defaultValue?: ComponentPropsWithoutRef<"textarea">["defaultValue"];
    textareaClassName?: string;
    value?: ComponentPropsWithoutRef<"textarea">["value"];
  };

function normalizeTextValue(value: string | number | readonly string[] | undefined) {
  if (value === undefined) {
    return "";
  }

  return String(value);
}

function setTextAreaRefValue(
  ref: ForwardedRef<HTMLTextAreaElement> | undefined,
  value: HTMLTextAreaElement | null
) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
}

const DsTextArea = forwardRef<HTMLTextAreaElement, DsTextAreaProps>(function DsTextArea(
  {
    "aria-describedby": ariaDescribedBy,
    className,
    defaultValue,
    disabled = false,
    errorMessage,
    id,
    maxLength,
    onChange,
    placeholder = "Digite aqui",
    required = false,
    textareaClassName,
    value,
    ...props
  },
  forwardedRef
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorMessageId = errorMessage ? `${inputId}-error-message` : undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(() => normalizeTextValue(defaultValue));

  const isControlled = value !== undefined;
  const selectedValue = isControlled ? normalizeTextValue(value) : uncontrolledValue;
  const describedBy = [ariaDescribedBy, errorMessageId].filter(Boolean).join(" ");
  const characterCount = maxLength ? `${selectedValue.length}/${maxLength}` : null;

  useEffect(() => {
    if (isControlled) {
      setUncontrolledValue(normalizeTextValue(value));
    }
  }, [isControlled, value]);

  function setInputRef(node: HTMLTextAreaElement | null) {
    setTextAreaRefValue(forwardedRef, node);
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    if (!isControlled) {
      setUncontrolledValue(event.currentTarget.value);
    }

    onChange?.(event);
  }

  const hasError = Boolean(errorMessage);

  return (
    <div className={cn("flex flex-col", className)} data-slot="text-area-field">
      <div
        className={cn(
          "flex min-h-[138px] flex-col justify-end rounded-[12px] bg-light-gray p-6",
          hasError ? "border border-feedback-danger" : "border border-transparent"
        )}
      >
        <textarea
          aria-describedby={describedBy || undefined}
          aria-invalid={errorMessage ? true : undefined}
          className={cn(
            "min-h-[90px] w-full resize-none border-none bg-transparent p-0 text-[14px] font-normal leading-[normal] tracking-[0px] text-dark-3 outline-none placeholder:font-semibold placeholder:text-gray-5 disabled:cursor-not-allowed disabled:text-gray-4",
            textareaClassName
          )}
          data-slot="text-area-input"
          disabled={disabled}
          id={inputId}
          maxLength={maxLength}
          onChange={handleChange}
          placeholder={placeholder}
          ref={setInputRef}
          required={required}
          value={selectedValue}
          {...props}
        />
        {characterCount ? (
          <p
            className="mt-2 text-right text-[12px] font-normal leading-[normal] tracking-[0px] text-gray-5"
            data-slot="text-area-counter"
          >
            {characterCount}
          </p>
        ) : null}
      </div>

      {errorMessage ? (
        <p
          className="mt-3 text-left text-[12px] font-normal leading-[normal] tracking-[0px] text-feedback-danger"
          data-slot="text-area-error"
          id={errorMessageId}
        >
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
});

DsTextArea.displayName = "DsTextArea";

export { DsTextArea };
export type { DsTextAreaProps };
